/**
 * TanStack Query hooks for the catalog feature. Keys are namespaced
 * "catalog/…"; cursor lists ride the shared ops-api helpers over the
 * `{ items, next_cursor }` envelope (limit ≤ 200 enforced by the wrapper).
 */
import { useQuery } from "@tanstack/react-query";
import {
  useCursorList,
  type CursorQueryResult,
  type ListFilters,
  type SortState,
} from "@/features/shared/ops-api";

import {
  getProduct,
  getProductImport,
  listProductImportRows,
  listProductImports,
  listProducts,
  listAllAttributes,
  listAllCategories,
  listAllTaxClasses,
  listAllUomConversions,
  listAllUoms,
  listActiveUoms,
  listProductAttributes,
  listProductComponents,
  listVariantBarcodes,
  listAllVariants,
  type Attribute,
  type Category,
  type ProductAttribute,
  type ProductComponent,
  type Product,
  type ProductImport,
  type ProductImportRow,
  type ProductStatus,
  type ImportRowStatus,
  type TaxClass,
  type Uom,
  type UomConversion,
  type Variant,
  type VariantBarcode,
} from "./api";

export const catalogKeys = {
  products: {
    all: ["catalog/products"] as const,
    list: (filters: Record<string, unknown>) => ["catalog/products", "list", filters] as const,
    detail: (id: string) => ["catalog/products", "detail", id] as const,
    components: (id: string) => ["catalog/products", "components", id] as const,
    attributes: (id: string) => ["catalog/products", "attributes", id] as const,
  },
  variants: {
    all: ["catalog/variants"] as const,
    search: (q: string) => ["catalog/variants", "search", q] as const,
    barcodes: (variantId: string) => ["catalog/variants", "barcodes", variantId] as const,
    barcodeCounts: (variantIds: readonly string[]) =>
      ["catalog/variants", "barcode-counts", variantIds] as const,
  },
  attributes: {
    all: ["catalog/attributes"] as const,
    /** The attributes screen's cursor pages. */
    list: () => ["catalog/attributes", "list"] as const,
    /** Every attribute, flattened — what a form needs to name an attribute it
     *  was given the id of. It is a different SHAPE from `list()`, so it must
     *  be a different key: one cache entry holding both means whichever screen
     *  was opened second reads the other's data and crashes on it. Both stay
     *  under `all`, so one invalidation still refreshes both. */
    every: () => ["catalog/attributes", "every"] as const,
  },
  uoms: {
    all: ["catalog/uoms"] as const,
    list: () => ["catalog/uoms", "list"] as const,
    active: () => ["catalog/uoms", "active"] as const,
    conversions: () => ["catalog/uoms", "conversions"] as const,
  },
  taxClasses: { list: () => ["catalog/tax-classes", "list"] as const },
  categories: { list: () => ["catalog/categories", "list"] as const },
  imports: {
    all: ["catalog/imports"] as const,
    list: (filters: Record<string, unknown>) => ["catalog/imports", "list", filters] as const,
    detail: (id: string) => ["catalog/imports", "detail", id] as const,
    rows: (id: string, filters: Record<string, unknown>) =>
      ["catalog/imports", "rows", id, filters] as const,
  },
};

// ── Products ─────────────────────────────────────────────────────────────

export interface ProductListFilters extends ListFilters {
  sku?: string;
  status?: ProductStatus;
  category_id?: string;
}

export function useProductList(
  filters: ProductListFilters,
  sort: SortState,
  limit?: number,
): CursorQueryResult<Product> {
  return useCursorList(
    catalogKeys.products.list({ ...filters, sort: sort?.key, dir: sort?.dir, limit }),
    ({ cursor, limit: pageLimit, sort: pageSort, signal }) =>
      listProducts(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
          ...(pageSort === null || pageSort === undefined
            ? {}
            : { sort: `${pageSort.key}:${pageSort.dir}` }),
        },
        signal,
      ),
    { sort, limit },
  );
}

/**
 * Atomic product body + ETag snapshot. Fetching has no side effect: the
 * editor explicitly accepts a snapshot before it can hydrate the form or
 * become the next PUT's If-Match validator.
 */
export type ProductDetailSnapshot = Awaited<ReturnType<typeof getProduct>>;

export function useProductDetail(productId: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.products.detail(productId ?? "new"),
    queryFn: (): Promise<ProductDetailSnapshot> => getProduct(productId as string),
    enabled: productId !== undefined,
  });
}

/** Attribute assignments for one product (drive the variant-grid columns). */
export function useProductAttributes(productId: string | undefined, enabled = true) {
  return useQuery<ProductAttribute[]>({
    queryKey: catalogKeys.products.attributes(productId ?? "new"),
    queryFn: ({ signal }) => listProductAttributes(productId as string, signal),
    enabled: productId !== undefined && enabled,
  });
}

/** Bundle/BOM components for one product (SPEC screen 6 grid). */
export function useProductComponents(productId: string | undefined, enabled = true) {
  return useQuery<ProductComponent[]>({
    queryKey: catalogKeys.products.components(productId ?? "new"),
    queryFn: ({ signal }) => listProductComponents(productId as string, signal),
    enabled: productId !== undefined && enabled,
  });
}

// ── Barcodes ─────────────────────────────────────────────────────────────

/** One variant's barcodes (Barcodes tab list). */
export function useVariantBarcodes(variantId: string | null, enabled = true) {
  return useQuery<VariantBarcode[]>({
    queryKey: catalogKeys.variants.barcodes(variantId ?? ""),
    queryFn: ({ signal }) => listVariantBarcodes(variantId as string, signal),
    enabled: variantId !== null && enabled,
  });
}

/** Barcode count per variant id (variant-grid "Barcodes" cell badges). */
export function useVariantBarcodeCounts(variantIds: readonly string[], enabled = true) {
  return useQuery<Record<string, number>>({
    queryKey: catalogKeys.variants.barcodeCounts(variantIds),
    queryFn: async ({ signal }) => {
      const entries = await Promise.all(
        variantIds.map(
          async (variantId) =>
            [variantId, (await listVariantBarcodes(variantId, signal)).length] as const,
        ),
      );
      return Object.fromEntries(entries);
    },
    enabled: variantIds.length > 0 && enabled,
  });
}

// ── Lookups ──────────────────────────────────────────────────────────────

export function useAttributes(enabled = true) {
  return useQuery<Attribute[]>({
    queryKey: catalogKeys.attributes.every(),
    queryFn: ({ signal }) => listAllAttributes(signal),
    enabled,
  });
}

export function useUoms(enabled = true) {
  return useQuery<Uom[]>({
    queryKey: catalogKeys.uoms.list(),
    queryFn: ({ signal }) => listAllUoms(signal),
    enabled,
  });
}

/** Server-filtered choices for references created from Catalog forms. */
export function useActiveUoms(enabled = true) {
  return useQuery<Uom[]>({
    queryKey: catalogKeys.uoms.active(),
    queryFn: ({ signal }) => listActiveUoms(signal),
    enabled,
  });
}

export function useUomConversions(enabled = true) {
  return useQuery<UomConversion[]>({
    queryKey: catalogKeys.uoms.conversions(),
    queryFn: ({ signal }) => listAllUomConversions(signal),
    enabled,
  });
}

export function useTaxClasses(enabled = true) {
  return useQuery<TaxClass[]>({
    queryKey: catalogKeys.taxClasses.list(),
    queryFn: ({ signal }) => listAllTaxClasses(signal),
    enabled,
  });
}

export function useCategories(enabled = true) {
  return useQuery<Category[]>({
    queryKey: catalogKeys.categories.list(),
    queryFn: ({ signal }) => listAllCategories(signal),
    enabled,
  });
}

/** All variants (≤200) for component pickers; optionally SKU-filtered. */
export function useVariantOptions(q = "", enabled = true) {
  return useQuery<Variant[]>({
    queryKey: catalogKeys.variants.search(q),
    queryFn: ({ signal }) => listAllVariants(q, signal),
    enabled,
  });
}

// ── Imports ──────────────────────────────────────────────────────────────

export function useImportsList(): CursorQueryResult<ProductImport> {
  return useCursorList(catalogKeys.imports.list({}), ({ cursor, limit, signal }) =>
    listProductImports(
      {
        ...(cursor === undefined || cursor === null ? {} : { cursor }),
        ...(limit === undefined ? {} : { limit }),
      },
      signal,
    ),
  );
}

/** Import statuses that keep the detail poller running (async server work). */
const POLLING_IMPORT_STATUSES = new Set(["uploaded", "validating", "committing"]);

/**
 * Import record; polls while the async validation or commit job runs so the
 * validating interstitial, the progress bar and the "safe to leave this page"
 * partial state stay honest (SPEC screen 7).
 */
export function useImportDetail(importId: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.imports.detail(importId ?? ""),
    queryFn: () => getProductImport(importId as string),
    enabled: importId !== undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status !== undefined && POLLING_IMPORT_STATUSES.has(status) ? 1500 : false;
    },
  });
}

export interface ImportRowFilters extends ListFilters {
  status?: ImportRowStatus;
  sku?: string;
}

export function useImportRows(
  importId: string | undefined,
  filters: ImportRowFilters,
  limit?: number,
): CursorQueryResult<ProductImportRow> {
  return useCursorList(
    catalogKeys.imports.rows(importId ?? "", { ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listProductImportRows(
        importId as string,
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit, enabled: importId !== undefined },
  );
}
